import { Box, Grid2, Grow, IconButton, Slider, Stack } from "@mui/material";
import { HorizontalOnlyContainer } from "../../../component/HorizontalOnlyContainer";
import { GameData, TeamTurns } from "../../../../domain/model/Game";
import {
  Answer,
  Question,
  QuestionType,
} from "../../../../domain/model/Question";
import {
  FullscreenExitRounded,
  FullscreenRounded,
  PauseRounded,
  PlayArrowRounded,
  RestoreRounded,
  StarRounded,
} from "@mui/icons-material";
import {
  COLOR_BLACK,
  COLOR_GOLD,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import { CustomTextView } from "../../../component/CustomTextView";
import { useEffect, useRef, useState } from "react";
import { QuestionViewModel } from "./QuestionViewModel";
import { CustomCardView } from "../../../component/CustomCardView";
import { formattedTime, getDifficultyInfo } from "../../../../core/utils/Utils";
import { LifelinesView } from "../../../component/LifelinesView";
import { CustomButtonView } from "../../../component/CustomButtonView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { QuestionMobileView } from "./QuestionMobileView";
import { useTranslation } from "react-i18next";
import { QuestionAnswerDetailsView } from "./QuestionAnswerDetailsView";
import { LifelineType } from "../../../../domain/model/Lifeline";
import { GiveFeedbackDialog } from "../../../component/GiveFeedbackDialog";
import ReactPlayer from "react-player";
import {
  provideGenerateQRCodeUseCase,
  provideQuestionGiveRatingUseCase,
} from "../../../di/UseCasesModule";
import { CustomImageView } from "../../../component/CustomImageView";
import { CharadesCategoryView } from "./CharadesCategoryView";

export function QuestionView({
  gameDataInput,
  questionInput,
  answerInput,
  onClosePopover,
  hasGivenRate,
  currentGameId,
  currentQuestionIndex,
}: {
  gameDataInput: GameData;
  onClosePopover: ({
    who,
    hasGivenRate,
  }: {
    who?: string;
    hasGivenRate: boolean;
  }) => void;
  questionInput: Question | undefined;
  answerInput: Answer | undefined;
  currentGameId: string | undefined;
  currentQuestionIndex: number | undefined;
  hasGivenRate: boolean;
}) {
  const viewModel = QuestionViewModel(
    provideQuestionGiveRatingUseCase(),
    provideGenerateQRCodeUseCase(),
    hasGivenRate,
    currentGameId,
    currentQuestionIndex
  );

  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() == "rtl";

  useEffect(() => {
    if (!questionInput || !answerInput) return;
    viewModel.init(gameDataInput, questionInput, answerInput);

    setChecked(true);
  }, []);

  const gameData = viewModel.gameData;
  const question = viewModel.question;
  const answer = viewModel.answer;

  const { height } = useWindowDimensions();
  const isMobile = height < 700;

  const lifelinePickedBefore =
    viewModel.findIfAnyLifelinesIsSelectedBeforeTheQuestion(
      gameData?.currentTeamTurn == TeamTurns.teamA
        ? gameData?.firstTeamLifelines
        : gameData?.secondTeamLifelines
    );

  const insuranceLifelineEnabled =
    (gameData?.currentTeamTurn == TeamTurns.teamA &&
      gameData?.firstTeamLifelines.filter(
        (l) => !l.disabled && l.selected && l.type == LifelineType.INSURANCE
      ).length > 0) ||
    (gameData?.currentTeamTurn == TeamTurns.teamB &&
      gameData?.secondTeamLifelines.filter(
        (l) => !l.disabled && l.selected && l.type == LifelineType.INSURANCE
      ).length > 0);

  const [checked, setChecked] = useState(false);

  return (
    <HorizontalOnlyContainer>
      {!gameData || !question || !answer ? (
        "ERROR"
      ) : (
        <Stack
          width={isMobile ? "95vw" : "98vw"}
          height={
            isMobile
              ? question.uncertainty != undefined
                ? "100vh"
                : "91vh"
              : "96vh"
          }
          alignItems={"center"}
          justifyContent={"center"}
          p={isMobile ? 1 : 2}
        >
          <Box width={"100%"} height={"100%"}>
            <GiveFeedbackDialog
              open={viewModel.giveFeedbackDialogOpen}
              onClose={() => viewModel.setGiveFeedbackDialogOpen(false)}
              onRateGiven={(rate, feedback) => {
                viewModel.giveRating(question._id!, { rate, feedback });
              }}
            />
            {(
              question?.type == null
                ? false
                : question?.type != QuestionType.NORMAL &&
                  !viewModel.videoFinished
            ) ? (
              <QuestionVideoOnlyView
                question={question}
                isMobile={false}
                onVideoEnded={() => viewModel.setVideoFinished(true)}
              />
            ) : (
              <>
                {isMobile ? (
                  <QuestionMobileView
                    onClosePopover={onClosePopover}
                    viewModel={viewModel}
                  />
                ) : (
                  <Stack width={"100%"} height={"100%"} gap={2}>
                    <Stack height={"85%"} width={"100%"}>
                      <Grid2
                        container
                        height={"100%"}
                        width={"100%"}
                        spacing={0}
                      >
                        <Grid2
                          size={3}
                          sx={{ pl: isRtl ? 2 : 0, pr: isRtl ? 0 : 2 }}
                        >
                          <Stack
                            width={"100%"}
                            height={"100%"}
                            alignItems={"center"}
                            gap={2}
                          >
                            <CustomCardView
                              width={"100%"}
                              height={"100%"}
                              children={
                                <Grow
                                  in={checked}
                                  {...(checked ? { timeout: 1000 } : {})}
                                  mountOnEnter
                                  unmountOnExit
                                >
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    height={"100%"}
                                    width={"100%"}
                                    justifyContent={"space-between"}
                                    position={"relative"}
                                    spacing={1}
                                    sx={{ direction: "ltr" }}
                                  >
                                    <Stack
                                      width={"100%"}
                                      height={"100%"}
                                      justifyContent={
                                        viewModel.bothTeamAnswered
                                          ? "center"
                                          : "start"
                                      }
                                      alignItems={
                                        viewModel.bothTeamAnswered
                                          ? "center"
                                          : "end"
                                      }
                                      gap={2}
                                    >
                                      <CustomTextView
                                        text={question.category?.title}
                                        fontWeight="bold"
                                        textAlign="center"
                                        variant={
                                          viewModel.bothTeamAnswered
                                            ? "h4"
                                            : "h5"
                                        }
                                      />
                                      <CustomTextView
                                        text={question.subcategoryName}
                                        fontWeight="bold"
                                        variant={
                                          viewModel.bothTeamAnswered
                                            ? "h5"
                                            : "h6"
                                        }
                                        textAlign="center"
                                        textColor={COLOR_LIGHT_GRAY}
                                      />
                                      <CustomTextView
                                        text={
                                          getDifficultyInfo(
                                            question.difficulty ?? "",
                                            t
                                          ).text
                                        }
                                        fontWeight="bold"
                                        variant={
                                          viewModel.bothTeamAnswered
                                            ? "h4"
                                            : "h5"
                                        }
                                        textAlign="center"
                                        textColor={
                                          getDifficultyInfo(
                                            question.difficulty ?? "",
                                            t
                                          ).color
                                        }
                                      />
                                    </Stack>

                                    <Stack
                                      position={"absolute"}
                                      width={"100%"}
                                      justifyContent={"center"}
                                      height={"100%"}
                                      zIndex={2}
                                    >
                                      {!viewModel.bothTeamAnswered && (
                                        <Stack width={"100%"} height={"40%"}>
                                          <CountdownTimer
                                            counter={
                                              viewModel.countdownTimerSeconds
                                            }
                                            setCounter={(c) =>
                                              viewModel.setCountdownTimerSeconds(
                                                c
                                              )
                                            }
                                            setPausedResumed={(is) => {
                                              viewModel.setTimerPausedOrResumed(
                                                is
                                              );
                                            }}
                                            hasFirstTeamAnswered={
                                              viewModel.hasFirstTeamAnswered
                                            }
                                          />
                                        </Stack>
                                      )}
                                    </Stack>
                                  </Stack>
                                </Grow>
                              }
                            />
                            <CustomCardView
                              width={"100%"}
                              height={"100%"}
                              children={
                                <Grow
                                  in={checked}
                                  {...(checked ? { timeout: 1000 } : {})}
                                  mountOnEnter
                                  unmountOnExit
                                >
                                  <Stack
                                    width={"100%"}
                                    height={"100%"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                    gap={4}
                                  >
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      gap={4}
                                    >
                                      <CustomTextView
                                        text={gameData.firstTeamName}
                                        variant="h4"
                                        fontWeight={
                                          gameData.currentTeamTurn ==
                                          TeamTurns.teamA
                                            ? "bold"
                                            : "normal"
                                        }
                                        textColor={COLOR_BLACK}
                                      />

                                      <CustomTextView
                                        text={gameData.firstTeamScore.toString()}
                                        fontWeight={
                                          gameData.currentTeamTurn ==
                                          TeamTurns.teamA
                                            ? "bold"
                                            : "normal"
                                        }
                                        variant="h3"
                                        textColor={COLOR_BLACK}
                                      />
                                    </Stack>
                                    {gameData.currentTeamTurn ==
                                      TeamTurns.teamA && (
                                      <LifelinesView
                                        lifelines={gameData.firstTeamLifelines}
                                        onLifelineClicked={
                                          lifelinePickedBefore
                                            ? undefined
                                            : (id) => {
                                                viewModel.onLifelineClicked(id);
                                              }
                                        }
                                        isMidQuestion={true}
                                        defaultColor={COLOR_MAIN}
                                        selectedColor={"rgba(216,5,49,0.9)"}
                                        disabledColor={COLOR_LIGHT_GRAY}
                                      />
                                    )}
                                  </Stack>
                                </Grow>
                              }
                            />
                          </Stack>
                        </Grid2>
                        <Grid2 size={6}>
                          <Stack
                            width={"100%"}
                            height={"100%"}
                            alignItems={"center"}
                            gap={2}
                          >
                            <CustomCardView
                              width={"100%"}
                              height={"85%"}
                              background={COLOR_MAIN}
                              children={
                                <>
                                  {viewModel.bothTeamAnswered &&
                                  viewModel.showWhoAnswered ? (
                                    <Stack
                                      width={"100%"}
                                      height={"100%"}
                                      justifyContent={"center"}
                                      alignItems={"center"}
                                      gap={1}
                                    >
                                      <Stack
                                        direction={"row"}
                                        width={"100%"}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                        gap={1}
                                      >
                                        {!(
                                          gameData.currentTeamTurn ==
                                            TeamTurns.teamB &&
                                          insuranceLifelineEnabled
                                        ) && (
                                          <Stack width={"100%"}>
                                            <CustomButtonView
                                              onClick={() => {
                                                onClosePopover({
                                                  who: gameData.firstTeamName,
                                                  hasGivenRate:
                                                    viewModel.isRatingGiven,
                                                });
                                              }}
                                              props={{ pt: 2, pb: 2 }}
                                              fontSize="h5"
                                              text={gameData.firstTeamName}
                                              backgroundColor="white"
                                              textColor={COLOR_MAIN}
                                            />
                                          </Stack>
                                        )}
                                        {!(
                                          gameData.currentTeamTurn ==
                                            TeamTurns.teamA &&
                                          insuranceLifelineEnabled
                                        ) && (
                                          <Stack width={"100%"}>
                                            <CustomButtonView
                                              fontSize="h5"
                                              props={{ pt: 2, pb: 2 }}
                                              onClick={() => {
                                                onClosePopover({
                                                  who: gameData.secondTeamName,
                                                  hasGivenRate:
                                                    viewModel.isRatingGiven,
                                                });
                                              }}
                                              text={gameData.secondTeamName}
                                              backgroundColor="white"
                                              textColor={COLOR_MAIN}
                                            />
                                          </Stack>
                                        )}
                                      </Stack>

                                      <Stack width={"100%"}>
                                        <CustomButtonView
                                          fontSize="h5"
                                          onClick={() => {
                                            onClosePopover({
                                              hasGivenRate:
                                                viewModel.isRatingGiven,
                                            });
                                          }}
                                          props={{ pt: 2, pb: 2 }}
                                          text={t("no_one_answered")}
                                          backgroundColor="white"
                                          textColor={COLOR_MAIN}
                                        />
                                      </Stack>
                                    </Stack>
                                  ) : viewModel.isCharadesCategory ||
                                    viewModel.isDrawingCategory ? (
                                    viewModel.showAnswer ? (
                                      <QuestionAnswerDetailsView
                                        question={question}
                                        answer={answer}
                                        showAnswer={viewModel.showAnswer}
                                        isMobile={false}
                                        showAttachment={
                                          question?.type == null
                                            ? true
                                            : question?.type ==
                                              QuestionType.TYPE_1
                                            ? viewModel.videoFinished
                                              ? true
                                              : false
                                            : question?.type ==
                                              QuestionType.NORMAL
                                        }
                                      />
                                    ) : (
                                      <>
                                        {viewModel.drawingImage ? (
                                          <CustomImageView
                                            src={viewModel.drawingImage}
                                            width={"100%"}
                                            height={"100%"}
                                            scale="contain"
                                            props={{ background: COLOR_WHITE, maxHeight: "60vh" }}
                                          />
                                        ) : (
                                          <CharadesCategoryView
                                            isCharadesReadyToPlay={
                                              viewModel.isReadyToPlay
                                            }
                                            qrCodeImageUrl={
                                              viewModel.qrCodeImage
                                            }
                                            onReadyButtonClicked={
                                              viewModel.readyButtonOnClick
                                            }
                                            isMobile={false}
                                          />
                                        )}
                                      </>
                                    )
                                  ) : (
                                    <QuestionAnswerDetailsView
                                      question={question}
                                      answer={answer}
                                      showAnswer={viewModel.showAnswer}
                                      isMobile={false}
                                      showAttachment={
                                        question?.type == null
                                          ? true
                                          : question?.type ==
                                            QuestionType.TYPE_1
                                          ? viewModel.videoFinished
                                            ? true
                                            : false
                                          : question?.type ==
                                            QuestionType.NORMAL
                                      }
                                    />
                                  )}
                                </>
                              }
                            />
                            <CustomCardView
                              width={"100%"}
                              height={"15%"}
                              background={COLOR_MAIN}
                              children={
                                <Stack
                                  width={"100%"}
                                  height={"100%"}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                  direction={"row"}
                                  gap={2}
                                >
                                  {!(
                                    viewModel.isCharadesCategory ||
                                    viewModel.isDrawingCategory
                                  ) ||
                                  ((viewModel.isCharadesCategory ||
                                    viewModel.isDrawingCategory) &&
                                    viewModel.isReadyToPlay) ? (
                                    <>
                                      {!viewModel.bothTeamAnswered && (
                                        <CustomButtonView
                                          onClick={() => {
                                            viewModel.onAnswerButtonClicked(
                                              insuranceLifelineEnabled
                                            );
                                          }}
                                          text={`${
                                            gameData.currentTeamTurn ==
                                            TeamTurns.teamA
                                              ? !viewModel.hasFirstTeamAnswered
                                                ? gameData.firstTeamName
                                                : gameData.secondTeamName
                                              : !viewModel.hasFirstTeamAnswered
                                              ? gameData.secondTeamName
                                              : gameData.firstTeamName
                                          } ${t("answered")}`}
                                          backgroundColor={
                                            COLOR_VERY_LIGHT_GRAY
                                          }
                                          textColor={COLOR_BLACK}
                                          props={{
                                            pl: 14,
                                            pr: 14,
                                            direction: "ltr",
                                          }}
                                          elevation={12}
                                          fontSize="h5"
                                        />
                                      )}

                                      {viewModel.bothTeamAnswered && (
                                        <Stack
                                          direction={"row"}
                                          alignItems={"center"}
                                          gap={2}
                                        >
                                          <CustomButtonView
                                            onClick={() => {
                                              viewModel.setShowAnswer(
                                                !viewModel.showAnswer
                                              );

                                              viewModel.setShowWhoAnswered(
                                                false
                                              );
                                            }}
                                            text={
                                              viewModel.showAnswer
                                                ? t("see_question")
                                                : t("see_answer")
                                            }
                                            backgroundColor={
                                              COLOR_VERY_LIGHT_GRAY
                                            }
                                            textColor={COLOR_BLACK}
                                            props={{ pl: 8, pr: 8 }}
                                            elevation={12}
                                            fontSize="h6"
                                          />
                                          {!viewModel.showWhoAnswered && (
                                            <CustomButtonView
                                              onClick={() => {
                                                viewModel.setShowWhoAnswered(
                                                  !viewModel.showWhoAnswered
                                                );
                                              }}
                                              text={t("who_answered")}
                                              backgroundColor={
                                                COLOR_VERY_LIGHT_GRAY
                                              }
                                              textColor={COLOR_BLACK}
                                              props={{ pl: 8, pr: 8 }}
                                              elevation={12}
                                              fontSize="h6"
                                            />
                                          )}
                                          {!viewModel.isRatingGiven && (
                                            <IconButton
                                              onClick={() => {
                                                viewModel.setGiveFeedbackDialogOpen(
                                                  true
                                                );
                                              }}
                                              sx={{
                                                border:
                                                  "solid 2px " + COLOR_GOLD,
                                                width: "3vw",
                                                height: "3vw",
                                              }}
                                            >
                                              <StarRounded
                                                sx={{ color: COLOR_GOLD }}
                                                fontSize="large"
                                              />
                                            </IconButton>
                                          )}
                                        </Stack>
                                      )}
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Stack>
                              }
                            />
                          </Stack>
                        </Grid2>

                        <Grid2
                          size={3}
                          sx={{ pl: isRtl ? 0 : 2, pr: isRtl ? 2 : 0 }}
                        >
                          <Stack
                            width={"100%"}
                            height={"100%"}
                            alignItems={"center"}
                            gap={2}
                          >
                            <CustomCardView
                              width={"100%"}
                              height={"100%"}
                              padding={6}
                              children={
                                <Stack
                                  width={"100%"}
                                  height={"100%"}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                >
                                  <CustomImageView
                                    width={"100%"}
                                    height={"100%"}
                                    props={{ borderRadius: 12 }}
                                    scale="fill"
                                    src={require("../../../../assets/images/platina_gif.gif")}
                                  />
                                </Stack>
                              }
                            />
                            <CustomCardView
                              width={"100%"}
                              height={"100%"}
                              children={
                                <Grow
                                  in={checked}
                                  {...(checked ? { timeout: 1000 } : {})}
                                  mountOnEnter
                                  unmountOnExit
                                >
                                  <Stack
                                    width={"100%"}
                                    height={"100%"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                    gap={4}
                                  >
                                    <Stack
                                      direction={"row"}
                                      alignItems={"center"}
                                      gap={4}
                                    >
                                      <CustomTextView
                                        text={gameData.secondTeamScore.toString()}
                                        fontWeight={
                                          gameData.currentTeamTurn ==
                                          TeamTurns.teamB
                                            ? "bold"
                                            : "normal"
                                        }
                                        textColor={COLOR_BLACK}
                                        variant="h3"
                                      />
                                      <CustomTextView
                                        text={gameData.secondTeamName}
                                        fontWeight={
                                          gameData.currentTeamTurn ==
                                          TeamTurns.teamB
                                            ? "bold"
                                            : "normal"
                                        }
                                        textColor={COLOR_BLACK}
                                        variant="h4"
                                      />
                                    </Stack>
                                    {gameData.currentTeamTurn ==
                                      TeamTurns.teamB && (
                                      <LifelinesView
                                        lifelines={gameData.secondTeamLifelines}
                                        onLifelineClicked={
                                          lifelinePickedBefore
                                            ? undefined
                                            : (id) => {
                                                viewModel.onLifelineClicked(id);
                                              }
                                        }
                                        isMidQuestion={true}
                                        defaultColor={COLOR_MAIN}
                                        selectedColor={"rgba(216,5,49,0.9)"}
                                        disabledColor={COLOR_LIGHT_GRAY}
                                      />
                                    )}
                                  </Stack>
                                </Grow>
                              }
                            />
                          </Stack>
                        </Grid2>
                      </Grid2>
                    </Stack>

                    <Stack height={"15%"} width={"100%"}>
                      <CustomCardView
                        width={"100%"}
                        height={"100%"}
                        background={COLOR_MAIN}
                        children={
                          <Stack
                            width={"100%"}
                            height={"100%"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            direction={"row"}
                            sx={{ direction: "ltr" }}
                          >
                            <Stack
                              width={"100%"}
                              direction={"row"}
                              alignItems={"center"}
                              justifyContent={"center"}
                            >
                              <CustomImageView
                                width={120}
                                height={250}
                                src={require("../../../../assets/images/rodeo_sponsor_image.png")}
                              />
                              <CustomImageView
                                width={80}
                                height={100}
                                scale="contain"
                                src={require("../../../../assets/images/gswf_sponsor_image.png")}
                                props={{ pt: 3.5 }}
                              />
                            </Stack>
                            <CustomImageView
                              width={"100%"}
                              height={80}
                              scale="contain"
                              src={require("../../../../assets/images/platina_logo_sponsor.png")}
                            />
                            {/* <CustomImageView
                              width={"100%"}
                              height={120}
                              scale="contain"
                              src={require("../../../../assets/images/q_wave_image2.png")}
                            /> */}
                          </Stack>
                        }
                      />
                    </Stack>
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Stack>
      )}
    </HorizontalOnlyContainer>
  );
}

export function QuestionVideoOnlyView({
  question,
  isMobile,
  onVideoEnded,
}: {
  question: Question;
  isMobile: boolean;
  onVideoEnded: () => void;
}) {
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [played, setPlayed] = useState(0);

  const height = isFullScreen ? "100%" : isMobile ? "60vh" : "40vh";
  const width = isFullScreen ? "100%" : isMobile ? "60vw" : "40vw";
  return (
    <Stack
      width={"100%"}
      height={"100%"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Stack
        height={height}
        width={width}
        position={"relative"}
        sx={{ borderRadius: 22 }}
      >
        <ReactPlayer
          width={"100%"}
          height={"100%"}
          style={{ background: COLOR_BLACK, borderRadius: 22 }}
          controls={false}
          ref={playerRef}
          playing={playing}
          onProgress={({ played }) => setPlayed(played)}
          stopOnUnmount
          onEnded={onVideoEnded}
          url={question.attachment?.downloadUrl}
        />
        {!playing && (
          <Stack
            justifyContent={"center"}
            alignItems={"center"}
            width={"100%"}
            height={"100%"}
            sx={{ position: "absolute", zIndex: 22 }}
          >
            <IconButton
              onClick={() => setPlaying(true)}
              sx={{
                background: COLOR_WHITE,
                "&:hover": {
                  background: COLOR_LIGHT_GRAY,
                  "@media (hover: none)": {
                    backgroundColor: COLOR_WHITE,
                  },
                },
              }}
            >
              <PlayArrowRounded sx={{ color: COLOR_BLACK }} />
            </IconButton>
          </Stack>
        )}

        <Stack sx={{ position: "absolute", zIndex: 23, p: 1 }}>
          <IconButton
            onClick={() => setIsFullScreen(!isFullScreen)}
            sx={{
              background: COLOR_WHITE,
              "&:hover": {
                background: COLOR_LIGHT_GRAY,
                "@media (hover: none)": {
                  backgroundColor: COLOR_WHITE,
                },
              },
            }}
          >
            {isFullScreen ? (
              <FullscreenExitRounded sx={{ color: COLOR_BLACK }} />
            ) : (
              <FullscreenRounded sx={{ color: COLOR_BLACK }} />
            )}
          </IconButton>
        </Stack>

        {playing && (
          <Stack
            sx={{
              position: "absolute",
              zIndex: 24,
              pb: 1,
              pl: 2,
              pr: 2,
              bgcolor: "rgba(0,0,0,0.2)",
              borderRadius: 22,
            }}
            width={"100%"}
            bottom={0}
          >
            <Slider value={played} min={0} max={1} step={0.01} disabled />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export function CountdownTimer({
  counter,
  setCounter,
  setPausedResumed,
  hasFirstTeamAnswered,
  isMobile = false,
}: {
  counter: number;
  setCounter: (count: number) => void;
  setPausedResumed: (paused: boolean) => void;
  hasFirstTeamAnswered: boolean;
  isMobile?: boolean;
}) {
  const [initialCounter, setInitialCounter] = useState(counter);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (counter > initialCounter) {
      setInitialCounter(counter);
    }
  }, [counter]);

  useEffect(() => {
    setPausedResumed(paused);
  }, [paused]);

  useEffect(() => {
    if (hasFirstTeamAnswered) {
      setInitialCounter(counter);
    }
  }, [hasFirstTeamAnswered]);

  useEffect(() => {
    const timer =
      counter > 0 &&
      setInterval(() => {
        if (paused) return;
        setCounter(counter - 1);
      }, 1000);
    if (timer) return () => clearInterval(timer);
  }, [counter, paused]);

  const handlePauseResumeCountdown = () => {
    setPaused(!paused);
  };

  const handleResetCountdown = () => {
    setCounter(initialCounter);
    setPaused(false);
  };

  return (
    <Stack
      gap={2}
      width={"100%"}
      height={"100%"}
      alignItems={"center"}
      justifyContent={"space-between"}
      direction={isMobile ? "row" : "column"}
      sx={{ direction: "ltr" }}
    >
      <Stack
        gap={1}
        width={isMobile ? "30%" : "100%"}
        height={"100%"}
        alignItems={isMobile ? "start" : "center"}
        direction={isMobile ? "column" : "row"}
        justifyContent={"center"}
      >
        <IconButton
          onClick={handleResetCountdown}
          sx={{ width: "4vw", height: "4vw" }}
        >
          <RestoreRounded
            sx={{
              color: isMobile ? COLOR_WHITE : COLOR_BLACK,
              width: "3vw",
              height: "3vw",
            }}
            fontSize={isMobile ? "small" : "large"}
          />
        </IconButton>

        <IconButton
          onClick={handlePauseResumeCountdown}
          sx={{ width: "4vw", height: "4vw" }}
        >
          {paused ? (
            <PlayArrowRounded
              sx={{
                color: isMobile ? COLOR_WHITE : COLOR_BLACK,
                width: "3vw",
                height: "3vw",
              }}
              fontSize={isMobile ? "small" : "large"}
            />
          ) : (
            <PauseRounded
              sx={{
                color: isMobile ? COLOR_WHITE : COLOR_BLACK,
                width: "3vw",
                height: "3vw",
              }}
              fontSize={isMobile ? "small" : "large"}
            />
          )}
        </IconButton>
      </Stack>

      <Stack
        width={isMobile ? "70%" : "100%"}
        height={"100%"}
        alignItems={isMobile ? "end" : "center"}
        justifyContent={"center"}
      >
        <CustomTextView
          text={counter > 0 ? formattedTime(counter) : "-"}
          variant={isMobile ? "h5" : "h3"}
          fontWeight="bold"
          textColor={isMobile ? COLOR_WHITE : COLOR_BLACK}
        />
      </Stack>
    </Stack>
  );
}
